import React from 'react';
import { useTranslation } from 'react-i18next';
import {
    PictureOutlined,
    LineChartOutlined,
    ArrowUpOutlined,
    InfoCircleOutlined,
    ArrowDownOutlined,
} from '@ant-design/icons';
import { Typography, Tag, Divider, Tooltip } from '@app/ui';
import './SocialBenchmarking.scss';
import { abbreviatedDecimal, ordinalSuffix } from '@app/utils/formatters';

const { Title, Text } = Typography;

type StatCardProps = {
    type: string;
    value: string;
    hasIcon?: boolean;
    metric?: string;
    avgDiff?: number;
    allRanked?: number;
    previousRank?: number;
    dateDiff?: number;
};

const StatCard = ({
    type,
    value,
    hasIcon,
    metric,
    avgDiff,
    allRanked,
    previousRank,
    dateDiff,
}: StatCardProps) => {
    const { t } = useTranslation('benchmarking');
    const valueFormatter = value => abbreviatedDecimal(value, value >= 1000);
    const isInTopHalf = allRanked && value && Number(value) <= allRanked / 2;
    const hasFooter = type === 'rank' || type === 'trend';

    const getTitle = (type: string) => {
        switch (type) {
            case 'total':
                return t('socialBenchmarking.statCard.total', { metric });
            case 'rank':
                return t('socialBenchmarking.statCard.rank');
            case 'trend':
                return t('socialBenchmarking.statCard.trend', { metric });
            default:
                return t('socialBenchmarking.statCard.average', { metric });
        }
    };

    const getPeriodicityText = () => {
        if (dateDiff === 7) {
            return t('socialBenchmarking.statCard.lastWeek');
        } else if (dateDiff === 30) {
            return t('socialBenchmarking.statCard.lastMonth');
        } else {
            return t('socialBenchmarking.statCard.lastDays', {
                days: dateDiff,
            });
        }
    };

    const getRankGrowthElement = () => {
        if (previousRank && value && previousRank > Number(value)) {
            const count = previousRank - Number(value);
            return (
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                    }}
                >
                    <ArrowUpOutlined
                        style={{ color: '#2DC850', fontSize: '20px' }}
                    />
                    <Text strong>
                        {t('socialBenchmarking.statCard.rankUp', { count })}
                    </Text>
                </div>
            );
        } else if (previousRank && value && previousRank < Number(value)) {
            const count = Number(value) - previousRank;
            return (
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                    }}
                >
                    <ArrowDownOutlined
                        style={{ color: '#d4047d', fontSize: '20px' }}
                    />
                    <Text strong>
                        {t('socialBenchmarking.statCard.rankDown', { count })}
                    </Text>
                </div>
            );
        } else {
            return (
                <Text strong>{t('socialBenchmarking.statCard.noChange')}</Text>
            );
        }
    };

    const getIcon = (type: string) => {
        switch (type) {
            case 'total':
                return <PictureOutlined style={{ color: '#009FDF' }} />;
            case 'average':
                return <LineChartOutlined style={{ color: '#009FDF' }} />;
            default:
                return null;
        }
    };

    const getValue = (type: string) => {
        switch (type) {
            case 'total':
                return (
                    <div className="value-wrapper">
                        <Title className="card-value" level={3}>
                            {value ? valueFormatter(value) : '-'}
                        </Title>
                        {value && (
                            <Tag
                                icon={
                                    avgDiff > 0 ? (
                                        <ArrowUpOutlined />
                                    ) : (
                                        <ArrowDownOutlined />
                                    )
                                }
                                color={avgDiff > 0 ? 'success' : 'error'}
                            >
                                {avgDiff > 0
                                    ? t(
                                          'socialBenchmarking.statCard.aboveAverage',
                                      )
                                    : t(
                                          'socialBenchmarking.statCard.belowAverage',
                                      )}
                            </Tag>
                        )}
                    </div>
                );
            case 'rank':
                return (
                    <div
                        className="rank-wrapper"
                        style={{
                            display: 'flex',
                            alignItems: 'baseline',
                            gap: '4px',
                        }}
                    >
                        <Title
                            className="rank-value"
                            level={3}
                            style={{
                                margin: 0,
                                lineHeight: 1,
                                color: isInTopHalf ? '#2DC850' : '#d4047d',
                            }}
                        >
                            {value && value !== 'N/A'
                                ? value + ordinalSuffix(value)
                                : '-'}
                        </Title>
                        <Title
                            className="rank-total"
                            style={{
                                margin: 0,
                                fontSize: '14px',
                                color: isInTopHalf ? '#2DC850' : '#d4047d',
                                lineHeight: 1,
                            }}
                        >
                            /{allRanked}
                        </Title>
                    </div>
                );
            case 'trend':
                return (
                    <div className="rank-wrapper">
                        {value && value !== '-' ? (
                            value.includes('+') ? (
                                <ArrowUpOutlined
                                    style={{
                                        color: '#2DC850',
                                        fontSize: '20px',
                                    }}
                                />
                            ) : (
                                <ArrowDownOutlined
                                    style={{
                                        color: '#d4047d',
                                        fontSize: '20px',
                                    }}
                                />
                            )
                        ) : null}
                        <Title
                            className="rank-value"
                            level={3}
                            style={{ margin: 0, lineHeight: 1 }}
                        >
                            {value || '-'}
                        </Title>
                    </div>
                );
            default:
                return (
                    <div className="value-wrapper">
                        <Title className="card-value" level={3}>
                            {valueFormatter(value)}
                        </Title>
                    </div>
                );
        }
    };

    const getFooter = (type: string) => {
        switch (type) {
            case 'rank':
                return (
                    <div className="stat-card-footer">
                        {getRankGrowthElement()}
                        <br />
                        <Text>
                            {t('socialBenchmarking.statCard.since', {
                                period: getPeriodicityText(),
                            })}
                        </Text>
                    </div>
                );
            case 'trend':
                return (
                    <div className="stat-card-footer">
                        <Text>
                            {t(
                                'socialBenchmarking.statCard.comparedToPrevious',
                            )}
                        </Text>
                        <br />
                        <Text strong>{dateDiff} days</Text>
                    </div>
                );
            default:
                return null;
        }
    };

    const title = getTitle(type);

    return (
        <div
            className="stat-card"
            style={{
                height: '140px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
            }}
        >
            {hasIcon && (
                <div className="icon-container">
                    <div className="icon-wrapper">{getIcon(type)}</div>
                </div>
            )}
            <div
                className="stat-card-content"
                style={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        width: '100%',
                    }}
                >
                    <Title
                        className="card-title"
                        level={5}
                        style={{ margin: 0 }}
                    >
                        {title}
                    </Title>
                    {type === 'rank' && (
                        <Tooltip
                            title={t('socialBenchmarking.statCard.rankTooltip')}
                        >
                            <InfoCircleOutlined
                                style={{
                                    color: '#999',
                                    fontSize: '14px',
                                    marginLeft: 'auto',
                                }}
                            />
                        </Tooltip>
                    )}
                </div>
                <div style={{ flex: 1, display: 'flex', alignItems: 'center' }}>
                    {getValue(type)}
                </div>
            </div>
            {hasFooter && value ? (
                <>
                    <Divider
                        style={{ margin: '0', color: '#F0F0F0' }}
                        type="horizontal"
                    />
                    {getFooter(type)}
                </>
            ) : (
                <div style={{ height: '40px' }} />
            )}
        </div>
    );
};

export default StatCard;
